import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/apiClient";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { createBrand, type BrandRecord } from "@/lib/catalog/brandsStore";
import { createVehicleModel, createVehicleVersion } from "@/lib/catalog/vehicleCatalogAdmin";
import { slugify } from "@/lib/slugify";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { ChevronRight, Image as ImageIcon, Loader2, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

function BrandLogoThumb({
  logoDataUrl,
  title,
  className,
}: {
  logoDataUrl: string | null | undefined;
  title?: string;
  className?: string;
}) {
  if (logoDataUrl) {
    return (
      <img
        src={logoDataUrl}
        alt={title ? `${title} logo` : ""}
        className={cn(
          "h-6 w-6 shrink-0 rounded object-contain ring-1 ring-slate-200/80 bg-white",
          className,
        )}
      />
    );
  }
  return (
    <div
      className={cn(
        "flex h-6 w-6 shrink-0 items-center justify-center rounded border border-dashed border-slate-300/80 bg-slate-50",
        className,
      )}
      aria-hidden
    >
      <ImageIcon className="h-3 w-3 text-slate-400" />
    </div>
  );
}

export const MODEL_YEARS_DESC: number[] = (() => {
  const end = 2026;
  const start = 1940;
  const out: number[] = [];
  for (let y = end; y >= start; y -= 1) out.push(y);
  return out;
})();

const MODEL_YEAR_MIN = 1900;
const MODEL_YEAR_MAX = 2100;

export function mergeModelYears(extraYears: number[]): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  for (const y of [...extraYears, ...MODEL_YEARS_DESC]) {
    if (!seen.has(y)) {
      seen.add(y);
      out.push(y);
    }
  }
  return out.sort((a, b) => b - a);
}

function parseModelYearInput(raw: string): number | null {
  const t = raw.trim();
  if (!/^\d{4}$/.test(t)) return null;
  const y = Number(t);
  if (!Number.isInteger(y) || y < MODEL_YEAR_MIN || y > MODEL_YEAR_MAX) return null;
  return y;
}

function catalogSaveErrorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error) && error.response?.data) {
    const data = error.response.data as { message?: string; errors?: Record<string, string[]> };
    if (data.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (data.message) return String(data.message);
  }
  return fallback;
}

type QuickAddKind = "year" | "make" | "model" | "version";

const STEPPER_ITEMS = [
  { title: "Model year" },
  { title: "Make" },
  { title: "Model" },
  { title: "Version", hint: "optional" as const },
] as const;

/** Slightly smaller than 0.75rem; normal weight applied at dialog root. */
const MODAL_TEXT = "text-[0.75rem] font-normal leading-snug antialiased";

function vpnRowCls(active: boolean) {
  return cn(
    "flex w-full cursor-pointer items-center gap-2 border-b border-border/60 px-2.5 py-2 text-left text-inherit font-normal text-foreground transition-colors",
    active
      ? "bg-amber-50 border-amber-200/70 dark:bg-amber-950/40 dark:border-amber-800/40"
      : "bg-background hover:bg-muted/30",
  );
}

function vpnChevronCls(active: boolean) {
  return cn(
    "h-3.5 w-3.5 shrink-0 text-muted-foreground/50",
    active && "text-amber-700 dark:text-amber-400",
  );
}

type VehicleModelRow = {
  id: number;
  brand_id: number;
  name: string;
  slug: string;
  is_popular: boolean;
  sort_order: number;
};

type VehicleVersionRow = {
  id: number;
  vehicle_model_id: number;
  name: string;
  slug: string;
  is_popular: boolean;
  sort_order: number;
};

function extractDataArray<T>(body: unknown): T[] {
  if (!body || typeof body !== "object") return [];
  const data = (body as Record<string, unknown>).data;
  return Array.isArray(data) ? (data as T[]) : [];
}

function splitPopular<T extends { name: string; is_popular?: boolean }>(rows: T[]): { popular: T[]; others: T[] } {
  const popular: T[] = [];
  const others: T[] = [];
  for (const r of rows) {
    if (r.is_popular) popular.push(r);
    else others.push(r);
  }
  popular.sort((a, b) => a.name.localeCompare(b.name));
  others.sort((a, b) => a.name.localeCompare(b.name));
  return { popular, others };
}

function splitBrands(brands: BrandRecord[]): { popular: BrandRecord[]; others: BrandRecord[] } {
  const popular: BrandRecord[] = [];
  const others: BrandRecord[] = [];
  for (const b of brands) {
    if (b.isPopular) popular.push(b);
    else others.push(b);
  }
  const order = ["Toyota", "Suzuki", "Honda", "Daihatsu", "Hyundai", "KIA", "Nissan", "Mazda"];
  popular.sort((a, b) => {
    const ia = order.findIndex((n) => n.toLowerCase() === a.name.trim().toLowerCase());
    const ib = order.findIndex((n) => n.toLowerCase() === b.name.trim().toLowerCase());
    const sa = ia === -1 ? 999 : ia;
    const sb = ib === -1 ? 999 : ib;
    if (sa !== sb) return sa - sb;
    return a.name.localeCompare(b.name);
  });
  others.sort((a, b) => a.name.localeCompare(b.name));
  return { popular, others };
}

/** Column titles above year / make / model / version pickers. */
function PakwheelsStepper({ activeIndex }: { activeIndex: number }) {
  return (
    <div
      className="grid shrink-0 grid-cols-4 divide-x divide-border/60 border-b border-border bg-muted/20"
      role="presentation"
    >
      {STEPPER_ITEMS.map((item, i) => {
        const done = i < activeIndex;
        const active = i === activeIndex;
        return (
          <div
            key={item.title}
            className={cn(
              "flex min-h-[3rem] min-w-0 flex-col items-center justify-center gap-0.5 px-2 py-2 sm:px-3",
              active && "bg-amber-50/90 dark:bg-amber-950/35",
              done && !active && "text-foreground",
              !done && !active && "text-muted-foreground",
            )}
          >
            <span className="line-clamp-2 text-center text-[0.8125rem] font-semibold leading-snug text-foreground">
              {item.title}
            </span>
            {"hint" in item && item.hint ? (
              <span className="text-[0.65rem] font-normal leading-none text-muted-foreground">({item.hint})</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function StickyColumnSearch({
  value,
  onChange,
  placeholder,
  disabled,
  inputId,
  showAddButton,
  onAddClick,
  addButtonLabel,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  disabled?: boolean;
  inputId?: string;
  showAddButton?: boolean;
  onAddClick?: () => void;
  addButtonLabel?: string;
}) {
  return (
    <div
      className={cn(
        "sticky top-0 z-[1] shrink-0 border-b border-border/70 bg-background/95 px-2 py-1.5 backdrop-blur-sm",
        "supports-[backdrop-filter]:bg-background/80",
      )}
    >
      <div className="flex items-center gap-1">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id={inputId}
            type="search"
            autoComplete="off"
            disabled={disabled}
            className="h-8 border-border/60 bg-background/90 pl-7 text-[0.75rem] shadow-sm"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
        {showAddButton && onAddClick ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-8 w-8 shrink-0"
            title={addButtonLabel ?? "Add new"}
            aria-label={addButtonLabel ?? "Add new"}
            onClick={onAddClick}
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

const VehicleNameYearColumn = memo(function VehicleNameYearColumn({
  selectedYear,
  search,
  onSearchChange,
  onPickYear,
  years,
  showAddButton,
  onAddClick,
}: {
  selectedYear: number | null;
  search: string;
  onSearchChange: (v: string) => void;
  onPickYear: (y: number) => void;
  years: number[];
  showAddButton?: boolean;
  onAddClick?: () => void;
}) {
  const deferredSearch = useDeferredValue(search);
  const filteredYears = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    if (!q) return years;
    return years.filter((y) => String(y).toLowerCase().includes(q));
  }, [deferredSearch, years]);
  const showNoMatch = deferredSearch.trim().length > 0 && filteredYears.length === 0;

  return (
    <div className="flex min-h-0 min-w-0 flex-col">
      <StickyColumnSearch
        value={search}
        onChange={onSearchChange}
        placeholder="Search year…"
        inputId="vpn-year-search"
        showAddButton={showAddButton}
        onAddClick={onAddClick}
        addButtonLabel="Add model year"
      />
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-background">
        {showNoMatch ? (
          <p className="p-3 text-center leading-relaxed text-muted-foreground">No matching years.</p>
        ) : (
          filteredYears.map((y) => (
            <button key={y} type="button" className={vpnRowCls(selectedYear === y)} onClick={() => onPickYear(y)}>
              <span className="flex-1 truncate">{y}</span>
              <ChevronRight className={vpnChevronCls(selectedYear === y)} aria-hidden />
            </button>
          ))
        )}
      </div>
    </div>
  );
});

const VehicleNameMakeColumn = memo(function VehicleNameMakeColumn({
  yearSelected,
  brands,
  brandId,
  search,
  onSearchChange,
  onPickBrand,
  showAddButton,
  onAddClick,
}: {
  yearSelected: boolean;
  brands: BrandRecord[];
  brandId: string | null;
  search: string;
  onSearchChange: (v: string) => void;
  onPickBrand: (b: BrandRecord) => void;
  showAddButton?: boolean;
  onAddClick?: () => void;
}) {
  const deferredSearch = useDeferredValue(search);
  const { popular: popularBrands, others: otherBrands } = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    const list = q ? brands.filter((b) => b.name.toLowerCase().includes(q)) : brands;
    return splitBrands(list);
  }, [brands, deferredSearch]);
  const empty = popularBrands.length === 0 && otherBrands.length === 0;
  const showNoMatch = empty && deferredSearch.trim().length > 0;

  return (
    <div className="flex min-h-0 min-w-0 flex-col">
      <StickyColumnSearch
        value={search}
        onChange={onSearchChange}
        placeholder={yearSelected ? "Search makes…" : "Select year first…"}
        disabled={!yearSelected}
        inputId="vpn-make-search"
        showAddButton={showAddButton && yearSelected}
        onAddClick={onAddClick}
        addButtonLabel="Add make"
      />
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-background">
        {!yearSelected ? (
          <p className="p-3 text-center leading-relaxed text-muted-foreground">Select a model year first.</p>
        ) : empty ? (
          <p className="p-3 text-center leading-relaxed text-muted-foreground">
            {showNoMatch ? "No matching makes." : "No makes in catalog."}
          </p>
        ) : (
          <>
            {popularBrands.length > 0 ? <SectionLabel variant="popular">Popular</SectionLabel> : null}
            {popularBrands.map((b) => (
              <button key={b.id} type="button" className={vpnRowCls(brandId === b.id)} onClick={() => onPickBrand(b)}>
                <BrandLogoThumb logoDataUrl={b.logoDataUrl} title={b.name} />
                <span className="min-w-0 flex-1 truncate">{b.name}</span>
                <ChevronRight className={vpnChevronCls(brandId === b.id)} aria-hidden />
              </button>
            ))}
            {otherBrands.length > 0 ? <SectionLabel variant="others">Others</SectionLabel> : null}
            {otherBrands.map((b) => (
              <button key={b.id} type="button" className={vpnRowCls(brandId === b.id)} onClick={() => onPickBrand(b)}>
                <BrandLogoThumb logoDataUrl={b.logoDataUrl} title={b.name} />
                <span className="min-w-0 flex-1 truncate">{b.name}</span>
                <ChevronRight className={vpnChevronCls(brandId === b.id)} aria-hidden />
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
});

const VehicleNameModelColumn = memo(function VehicleNameModelColumn({
  yearSelected,
  brandId,
  models,
  modelsLoading,
  modelId,
  search,
  onSearchChange,
  onPickModel,
  showAddButton,
  onAddClick,
}: {
  yearSelected: boolean;
  brandId: string | null;
  models: VehicleModelRow[];
  modelsLoading: boolean;
  modelId: number | null;
  search: string;
  onSearchChange: (v: string) => void;
  onPickModel: (m: VehicleModelRow) => void;
  showAddButton?: boolean;
  onAddClick?: () => void;
}) {
  const deferredSearch = useDeferredValue(search);
  const { popular: popularModels, others: otherModels } = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    const list = q ? models.filter((m) => m.name.toLowerCase().includes(q)) : models;
    return splitPopular(list);
  }, [models, deferredSearch]);
  const listEmpty = popularModels.length === 0 && otherModels.length === 0;

  return (
    <div className="flex min-h-0 min-w-0 flex-col">
      <StickyColumnSearch
        value={search}
        onChange={onSearchChange}
        placeholder={
          !yearSelected ? "Select year first…" : !brandId ? "Select make first…" : "Search models…"
        }
        disabled={!yearSelected || !brandId}
        inputId="vpn-model-search"
        showAddButton={showAddButton && yearSelected && Boolean(brandId) && !modelsLoading}
        onAddClick={onAddClick}
        addButtonLabel="Add model"
      />
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-background">
        {!yearSelected ? (
          <p className="p-3 text-center leading-relaxed text-muted-foreground">Select a model year first.</p>
        ) : !brandId ? (
          <p className="p-3 text-center leading-relaxed text-muted-foreground">Select a make first.</p>
        ) : modelsLoading ? (
          <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 py-10">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Loading models…</span>
          </div>
        ) : models.length === 0 ? (
          <p className="p-3 text-center leading-relaxed text-muted-foreground">No models for this make yet.</p>
        ) : listEmpty ? (
          <p className="p-3 text-center leading-relaxed text-muted-foreground">No matching models.</p>
        ) : (
          <>
            {popularModels.length > 0 ? <SectionLabel variant="popular">Popular</SectionLabel> : null}
            {popularModels.map((m) => (
              <button key={m.id} type="button" className={vpnRowCls(modelId === m.id)} onClick={() => onPickModel(m)}>
                <span className="min-w-0 flex-1 truncate">{m.name}</span>
                <ChevronRight className={vpnChevronCls(modelId === m.id)} aria-hidden />
              </button>
            ))}
            {otherModels.length > 0 ? <SectionLabel variant="others">Others</SectionLabel> : null}
            {otherModels.map((m) => (
              <button key={m.id} type="button" className={vpnRowCls(modelId === m.id)} onClick={() => onPickModel(m)}>
                <span className="min-w-0 flex-1 truncate">{m.name}</span>
                <ChevronRight className={vpnChevronCls(modelId === m.id)} aria-hidden />
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
});

const VehicleNameVersionColumn = memo(function VehicleNameVersionColumn({
  yearSelected,
  modelId,
  versions,
  versionsLoading,
  versionId,
  search,
  onSearchChange,
  onPickVersion,
  showAddButton,
  onAddClick,
}: {
  yearSelected: boolean;
  modelId: number | null;
  versions: VehicleVersionRow[];
  versionsLoading: boolean;
  versionId: number | null;
  search: string;
  onSearchChange: (v: string) => void;
  onPickVersion: (v: VehicleVersionRow) => void;
  showAddButton?: boolean;
  onAddClick?: () => void;
}) {
  const deferredSearch = useDeferredValue(search);
  const { popular: popularVersions, others: otherVersions } = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    const list = q ? versions.filter((v) => v.name.toLowerCase().includes(q)) : versions;
    return splitPopular(list);
  }, [versions, deferredSearch]);
  const listEmpty = popularVersions.length === 0 && otherVersions.length === 0;

  return (
    <div className="flex min-h-0 min-w-0 flex-col">
      <StickyColumnSearch
        value={search}
        onChange={onSearchChange}
        placeholder={
          !yearSelected ? "Select year first…" : modelId == null ? "Select model first…" : "Search versions…"
        }
        disabled={!yearSelected || modelId == null}
        inputId="vpn-version-search"
        showAddButton={showAddButton && yearSelected && modelId != null && !versionsLoading}
        onAddClick={onAddClick}
        addButtonLabel="Add version"
      />
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-background">
        {!yearSelected ? (
          <p className="p-3 text-center leading-relaxed text-muted-foreground">Select a model year first.</p>
        ) : modelId == null ? (
          <p className="p-3 text-center leading-relaxed text-muted-foreground">Select a model first.</p>
        ) : versionsLoading ? (
          <div className="flex min-h-[220px] flex-col items-center justify-center gap-2 py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Loading versions…</span>
          </div>
        ) : versions.length === 0 ? (
          <p className="p-3 text-center leading-relaxed text-muted-foreground">
            No versions for this model yet.
          </p>
        ) : listEmpty ? (
          <p className="p-3 text-center leading-relaxed text-muted-foreground">No matching versions.</p>
        ) : (
          <>
            {popularVersions.length > 0 ? <SectionLabel variant="popular">Popular</SectionLabel> : null}
            {popularVersions.map((v) => (
              <button
                key={v.id}
                type="button"
                className={vpnRowCls(versionId === v.id)}
                onClick={() => onPickVersion(v)}
              >
                <span className="min-w-0 flex-1 truncate">{v.name}</span>
                <ChevronRight className={vpnChevronCls(versionId === v.id)} aria-hidden />
              </button>
            ))}
            {otherVersions.length > 0 ? <SectionLabel variant="others">Others</SectionLabel> : null}
            {otherVersions.map((v) => (
              <button
                key={v.id}
                type="button"
                className={vpnRowCls(versionId === v.id)}
                onClick={() => onPickVersion(v)}
              >
                <span className="min-w-0 flex-1 truncate">{v.name}</span>
                <ChevronRight className={vpnChevronCls(versionId === v.id)} aria-hidden />
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
});

function SectionLabel({ variant, children }: { variant: "popular" | "others"; children: ReactNode }) {
  return (
    <div
      className={cn(
        "border-y border-border px-2.5 py-1.5 font-display text-inherit font-normal uppercase tracking-wide text-muted-foreground",
        variant === "popular" ? "bg-muted/55" : "bg-muted/35",
      )}
    >
      {children}
    </div>
  );
}

type ApplyPayload = {
  name: string;
  brandId: string;
  vehicleModelId: string | null;
  vehicleVersionId: string | null;
  modelYear: number;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brands: BrandRecord[];
  initialBrandId?: string | null;
  initialModelYear?: number | null;
  initialVehicleModelId?: string | null;
  initialVehicleVersionId?: string | null;
  onApply: (payload: ApplyPayload) => void;
  onBrandCreated?: (brand: BrandRecord) => void;
};

function mapVehicleModelRow(row: {
  id: number;
  brand_id: number;
  name: string;
  slug: string;
  is_popular: boolean;
  sort_order: number;
}): VehicleModelRow {
  return {
    id: row.id,
    brand_id: row.brand_id,
    name: row.name,
    slug: row.slug,
    is_popular: row.is_popular,
    sort_order: row.sort_order,
  };
}

function mapVehicleVersionRow(row: {
  id: number;
  vehicle_model_id: number;
  name: string;
  slug: string;
  is_popular: boolean;
  sort_order: number;
}): VehicleVersionRow {
  return {
    id: row.id,
    vehicle_model_id: row.vehicle_model_id,
    name: row.name,
    slug: row.slug,
    is_popular: row.is_popular,
    sort_order: row.sort_order,
  };
}

function parsePositiveIntId(raw: string | null | undefined): number | null {
  if (raw == null || String(raw).trim() === "") return null;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

export function VehicleProductNameModal({
  open,
  onOpenChange,
  brands,
  initialBrandId,
  initialModelYear,
  initialVehicleModelId,
  initialVehicleVersionId,
  onApply,
  onBrandCreated,
}: Props) {
  const canCreateCatalog = can(getStoredAuthUser(), "brand_can_create");

  const [year, setYear] = useState<number | null>(null);
  const [brandId, setBrandId] = useState<string | null>(null);
  const [modelId, setModelId] = useState<number | null>(null);
  const [versionId, setVersionId] = useState<number | null>(null);
  const [extraYears, setExtraYears] = useState<number[]>([]);

  const [models, setModels] = useState<VehicleModelRow[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [versions, setVersions] = useState<VehicleVersionRow[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  const [yearSearch, setYearSearch] = useState("");
  const [makeSearch, setMakeSearch] = useState("");
  const [modelSearch, setModelSearch] = useState("");
  const [versionSearch, setVersionSearch] = useState("");

  const [quickAddKind, setQuickAddKind] = useState<QuickAddKind | null>(null);
  const [quickAddValue, setQuickAddValue] = useState("");
  const [quickAddSaving, setQuickAddSaving] = useState(false);

  const availableYears = useMemo(() => mergeModelYears(extraYears), [extraYears]);

  /** Incremented on user-driven year/make/model changes so stale open-hydration does not overwrite picks. */
  const interactionGenRef = useRef(0);

  const selectedBrand = useMemo(() => brands.find((b) => b.id === brandId), [brands, brandId]);
  const selectedModel = useMemo(() => models.find((m) => m.id === modelId), [models, modelId]);
  const selectedVersion = useMemo(() => versions.find((v) => v.id === versionId), [versions, versionId]);

  const activeStepIndex = useMemo(() => {
    if (year == null) return 0;
    if (!brandId) return 1;
    if (modelId == null) return 2;
    return 3;
  }, [year, brandId, modelId]);

  const loadModels = useCallback(async (bid: string): Promise<VehicleModelRow[]> => {
    setModelsLoading(true);
    setModels([]);
    try {
      const res = await api.get(`/api/v1/catalog/brands/${bid}/vehicle-models`);
      const list = extractDataArray<VehicleModelRow>(res.data);
      setModels(list);
      return list;
    } catch {
      setModels([]);
      return [];
    } finally {
      setModelsLoading(false);
    }
  }, []);

  const loadVersions = useCallback(async (mid: number): Promise<VehicleVersionRow[]> => {
    setVersionsLoading(true);
    setVersions([]);
    try {
      const res = await api.get(`/api/v1/catalog/vehicle-models/${mid}/vehicle-versions`);
      const list = extractDataArray<VehicleVersionRow>(res.data);
      setVersions(list);
      return list;
    } catch {
      setVersions([]);
      return [];
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  const handlePickYear = useCallback((y: number) => {
    interactionGenRef.current += 1;
    setYear(y);
  }, []);

  const handlePickBrand = useCallback(
    (b: BrandRecord) => {
      interactionGenRef.current += 1;
      setBrandId(b.id);
      setModelId(null);
      setVersionId(null);
      setVersions([]);
      void loadModels(b.id);
    },
    [loadModels],
  );

  const handlePickModel = useCallback(
    (m: VehicleModelRow) => {
      interactionGenRef.current += 1;
      setModelId(m.id);
      setVersionId(null);
      void loadVersions(m.id);
    },
    [loadVersions],
  );

  const handlePickVersion = useCallback(
    (v: VehicleVersionRow) => {
      interactionGenRef.current += 1;
      setVersionId(v.id);

      if (year != null && brandId && selectedBrand && selectedModel) {
        const parts = [selectedBrand.name, selectedModel.name];
        const ver = v.name?.trim();
        if (ver) parts.push(ver);
        parts.push(String(year));
        const built = parts.join(" ");

        onApply({
          name: built,
          brandId,
          vehicleModelId: String(selectedModel.id),
          vehicleVersionId: String(v.id),
          modelYear: year,
        });
        onOpenChange(false);
      }
    },
    [year, brandId, selectedBrand, selectedModel, onApply, onOpenChange],
  );

  const openQuickAdd = useCallback((kind: QuickAddKind, initialValue: string) => {
    setQuickAddKind(kind);
    setQuickAddValue(initialValue.trim());
  }, []);

  const closeQuickAdd = useCallback(() => {
    if (quickAddSaving) return;
    setQuickAddKind(null);
    setQuickAddValue("");
  }, [quickAddSaving]);

  const handleQuickAddSave = useCallback(async () => {
    if (!quickAddKind || quickAddSaving) return;

    if (quickAddKind === "year") {
      const parsed = parseModelYearInput(quickAddValue);
      if (parsed == null) {
        toast.error(`Enter a valid model year (${MODEL_YEAR_MIN}–${MODEL_YEAR_MAX}).`);
        return;
      }
      setQuickAddSaving(true);
      try {
        setExtraYears((prev) => (prev.includes(parsed) ? prev : [...prev, parsed]));
        interactionGenRef.current += 1;
        setYear(parsed);
        setYearSearch("");
        setQuickAddKind(null);
        setQuickAddValue("");
      } finally {
        setQuickAddSaving(false);
      }
      return;
    }

    const name = quickAddValue.trim();
    if (!name) {
      toast.error("Name is required.");
      return;
    }

    setQuickAddSaving(true);
    try {
      if (quickAddKind === "make") {
        const result = await createBrand({ name, key: name });
        onBrandCreated?.(result.record);
        handlePickBrand(result.record);
        setMakeSearch("");
        toast.success(result.message || "Make created.");
      } else if (quickAddKind === "model") {
        if (!brandId) {
          toast.error("Select a make first.");
          return;
        }
        const created = await createVehicleModel({
          brand_id: Number(brandId),
          name,
          slug: slugify(name) || `model-${Date.now()}`,
          is_popular: false,
          sort_order: 0,
        });
        const row = mapVehicleModelRow(created);
        setModels((prev) => [...prev, row]);
        handlePickModel(row);
        setModelSearch("");
        toast.success("Model created.");
      } else if (quickAddKind === "version") {
        if (modelId == null) {
          toast.error("Select a model first.");
          return;
        }
        const created = await createVehicleVersion({
          vehicle_model_id: modelId,
          name,
          slug: slugify(name) || `version-${Date.now()}`,
          is_popular: false,
          sort_order: 0,
        });
        const row = mapVehicleVersionRow(created);
        setVersions((prev) => [...prev, row]);
        handlePickVersion(row);
        setVersionSearch("");
        toast.success("Version created.");
      }
      setQuickAddKind(null);
      setQuickAddValue("");
    } catch (error) {
      console.error("[VehicleProductNameModal] quick add failed", error);
      const fallback =
        quickAddKind === "make"
          ? "Could not create make."
          : quickAddKind === "model"
            ? "Could not create model."
            : "Could not create version.";
      toast.error(catalogSaveErrorMessage(error, fallback));
    } finally {
      setQuickAddSaving(false);
    }
  }, [
    quickAddKind,
    quickAddSaving,
    quickAddValue,
    brandId,
    modelId,
    onBrandCreated,
    handlePickBrand,
    handlePickModel,
    handlePickVersion,
  ]);

  useEffect(() => {
    if (!open) {
      setYear(null);
      setBrandId(null);
      setModelId(null);
      setVersionId(null);
      setExtraYears([]);
      setModels([]);
      setVersions([]);
      setModelsLoading(false);
      setVersionsLoading(false);
      setYearSearch("");
      setMakeSearch("");
      setModelSearch("");
      setVersionSearch("");
      setQuickAddKind(null);
      setQuickAddValue("");
      setQuickAddSaving(false);
    }
  }, [open]);

  useEffect(() => {
    setModelSearch("");
  }, [brandId]);

  useEffect(() => {
    setVersionSearch("");
  }, [modelId]);

  useEffect(() => {
    if (!open) return;

    interactionGenRef.current = 0;
    const genAtOpen = interactionGenRef.current;
    let cancelled = false;

    const run = async () => {
      let yInit: number | null = null;
      let extra: number[] = [];
      if (initialModelYear != null && Number.isFinite(initialModelYear)) {
        const y = Math.trunc(initialModelYear);
        if (y >= MODEL_YEAR_MIN && y <= MODEL_YEAR_MAX) {
          yInit = y;
          if (!MODEL_YEARS_DESC.includes(y)) {
            extra = [y];
          }
        }
      }
      setExtraYears(extra);
      const bid = initialBrandId && String(initialBrandId).trim() !== "" ? String(initialBrandId) : null;
      const wantModel = parsePositiveIntId(initialVehicleModelId ?? undefined);
      const wantVersion = parsePositiveIntId(initialVehicleVersionId ?? undefined);

      setYear(yInit);
      const gatedBrandId = yInit == null ? null : bid;
      setBrandId(gatedBrandId);
      setModelId(null);
      setVersionId(null);
      setModels([]);
      setVersions([]);

      if (!gatedBrandId) return;

      const modelList = await loadModels(gatedBrandId);
      if (cancelled || interactionGenRef.current !== genAtOpen) return;

      const validModel = wantModel != null && modelList.some((m) => m.id === wantModel) ? wantModel : null;
      setModelId(validModel);

      if (validModel == null) {
        setVersions([]);
        setVersionId(null);
        return;
      }

      const versionList = await loadVersions(validModel);
      if (cancelled || interactionGenRef.current !== genAtOpen) return;

      const validVersion =
        wantVersion != null && versionList.some((v) => v.id === wantVersion) ? wantVersion : null;
      setVersionId(validVersion);
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [open, initialBrandId, initialModelYear, initialVehicleModelId, initialVehicleVersionId, loadModels, loadVersions]);

  const builtName = useMemo(() => {
    if (year == null || !selectedBrand || !selectedModel) return "";
    const v = selectedVersion?.name?.trim();
    const parts = [selectedBrand.name, selectedModel.name];
    if (v) parts.push(v);
    parts.push(String(year));
    return parts.join(" ");
  }, [year, selectedBrand, selectedModel, selectedVersion]);

  const canApply = year != null && Boolean(selectedBrand) && Boolean(selectedModel);

  const handleApply = () => {
    if (!canApply || year == null || !brandId || !selectedBrand || !selectedModel) return;
    onApply({
      name: builtName,
      brandId,
      vehicleModelId: String(selectedModel.id),
      vehicleVersionId: selectedVersion ? String(selectedVersion.id) : null,
      modelYear: year,
    });
    onOpenChange(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex max-h-[min(92vh,780px)] max-w-[min(96vw,1120px)] flex-col gap-0 overflow-hidden p-0 sm:rounded-xl",
          "border-border/60 bg-background shadow-2xl",
          MODAL_TEXT,
        )}
        hideCloseButton
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Model year, make, model, and version</DialogTitle>
        <div className="flex min-h-0 flex-1 flex-col">
          <PakwheelsStepper activeIndex={activeStepIndex} />

          <div className="grid min-h-[min(440px,58vh)] flex-1 grid-cols-4 divide-x divide-border/60 bg-background min-h-0">
            <VehicleNameYearColumn
              selectedYear={year}
              search={yearSearch}
              onSearchChange={setYearSearch}
              onPickYear={handlePickYear}
              years={availableYears}
              showAddButton
              onAddClick={() => openQuickAdd("year", yearSearch)}
            />
            <VehicleNameMakeColumn
              yearSelected={year != null}
              brands={brands}
              brandId={brandId}
              search={makeSearch}
              onSearchChange={setMakeSearch}
              onPickBrand={handlePickBrand}
              showAddButton={canCreateCatalog}
              onAddClick={() => openQuickAdd("make", makeSearch)}
            />
            <VehicleNameModelColumn
              yearSelected={year != null}
              brandId={brandId}
              models={models}
              modelsLoading={modelsLoading}
              modelId={modelId}
              search={modelSearch}
              onSearchChange={setModelSearch}
              onPickModel={handlePickModel}
              showAddButton={canCreateCatalog}
              onAddClick={() => openQuickAdd("model", modelSearch)}
            />
            <VehicleNameVersionColumn
              yearSelected={year != null}
              modelId={modelId}
              versions={versions}
              versionsLoading={versionsLoading}
              versionId={versionId}
              search={versionSearch}
              onSearchChange={setVersionSearch}
              onPickVersion={handlePickVersion}
              showAddButton={canCreateCatalog}
              onAddClick={() => openQuickAdd("version", versionSearch)}
            />
          </div>
        </div>

        <div className="relative z-10 flex flex-col items-center gap-2 border-t border-border bg-muted/20 px-4 py-3 sm:px-6">
          {builtName ? (
            <p className="w-full max-w-2xl truncate text-center text-foreground/90" title={builtName}>
              <span className="uppercase tracking-wide text-muted-foreground">Preview </span>
              <span className="text-foreground">{builtName}</span>
            </p>
          ) : (
            <p className="text-center text-muted-foreground">
              Choose year, make, and model to preview the product name.
            </p>
          )}
          <Button
            type="button"
            variant="default"
            disabled={!canApply}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleApply();
            }}
            className="h-9 w-full max-w-md text-[0.6875rem] font-normal"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>

      <Dialog open={quickAddKind != null} onOpenChange={(o) => !o && closeQuickAdd()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {quickAddKind === "year"
                ? "Add model year"
                : quickAddKind === "make"
                  ? "Add make"
                  : quickAddKind === "model"
                    ? "Add model"
                    : "Add version"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs text-muted-foreground" htmlFor="vpn-quick-add-value">
              {quickAddKind === "year" ? "Model year" : "Name"}
            </Label>
            <Input
              id="vpn-quick-add-value"
              className="glass-input h-10"
              type={quickAddKind === "year" ? "number" : "text"}
              min={quickAddKind === "year" ? MODEL_YEAR_MIN : undefined}
              max={quickAddKind === "year" ? MODEL_YEAR_MAX : undefined}
              value={quickAddValue}
              onChange={(e) => setQuickAddValue(e.target.value)}
              placeholder={
                quickAddKind === "year"
                  ? `e.g. ${MODEL_YEAR_MAX}`
                  : quickAddKind === "make"
                    ? "e.g. Toyota"
                    : quickAddKind === "model"
                      ? "e.g. Corolla"
                      : "e.g. Altis"
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleQuickAddSave();
                }
              }}
            />
            {quickAddKind === "model" && selectedBrand ? (
              <p className="text-xs text-muted-foreground">
                Make: <span className="text-foreground">{selectedBrand.name}</span>
              </p>
            ) : null}
            {quickAddKind === "version" && selectedModel ? (
              <p className="text-xs text-muted-foreground">
                Model: <span className="text-foreground">{selectedModel.name}</span>
              </p>
            ) : null}
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={closeQuickAdd} disabled={quickAddSaving}>
              Cancel
            </Button>
            <Button type="button" variant="default" disabled={quickAddSaving} onClick={() => void handleQuickAddSave()}>
              {quickAddSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
